import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Printer, RotateCw } from "lucide-react";

const PRESETS = [
  { w: 50, h: 30, label: "50x30" },
  { w: 62, h: 29, label: "62x29" },
  { w: 62, h: 42, label: "62x42" },
  { w: 70, h: 40, label: "70x40" },
  { w: 100, h: 50, label: "100x50" },
];

/**
 * Dialog impostazioni formato etichetta HACCP (mm) con presets, anteprima e toggle rotazione.
 */
const LabelSettingsDialog = ({ open, onOpenChange, tempSize, setTempSize, onSave }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Printer className="h-5 w-5 text-primary" />
            Formato Etichetta
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Imposta le dimensioni in base alla tua stampante e al formato carta.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Larghezza (mm)</Label>
              <Input
                type="number"
                min="20"
                max="150"
                value={tempSize.width}
                onChange={(e) => setTempSize((prev) => ({ ...prev, width: e.target.value }))}
                className="h-11 text-center text-lg font-bold"
                data-testid="label-width-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Altezza (mm)</Label>
              <Input
                type="number"
                min="15"
                max="100"
                value={tempSize.height}
                onChange={(e) => setTempSize((prev) => ({ ...prev, height: e.target.value }))}
                className="h-11 text-center text-lg font-bold"
                data-testid="label-height-input"
              />
            </div>
          </div>

          {/* Anteprima proporzione */}
          <div className="flex flex-col items-center gap-2">
            <p className="text-xs text-muted-foreground">Anteprima proporzione</p>
            <div
              className="border-2 border-dashed border-primary/40 rounded-md bg-primary/5 flex items-center justify-center"
              style={{
                width: `${Math.min(200, Math.max(60, Number(tempSize.rotated ? tempSize.height : tempSize.width) * 2.5))}px`,
                height: `${Math.min(120, Math.max(40, Number(tempSize.rotated ? tempSize.width : tempSize.height) * 2.5))}px`,
                transition: "all 0.2s ease",
              }}
            >
              <span
                className={`text-xs font-mono text-primary/60 ${tempSize.rotated ? "rotate-90" : ""}`}
                style={{ transition: "transform 0.2s ease" }}
              >
                {tempSize.width}x{tempSize.height}
              </span>
            </div>
          </div>

          {/* Toggle rotazione */}
          <button
            type="button"
            onClick={() => setTempSize((prev) => ({ ...prev, rotated: !prev.rotated }))}
            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border transition-colors ${
              tempSize.rotated
                ? "bg-primary/10 border-primary/30 text-primary"
                : "bg-muted border-border text-muted-foreground"
            }`}
            data-testid="label-rotate-toggle"
          >
            <div className="flex items-center gap-2">
              <RotateCw className="h-4 w-4" />
              <span className="text-sm font-medium">Ruota 90°</span>
            </div>
            <div
              className={`w-9 h-5 rounded-full flex items-center transition-colors ${
                tempSize.rotated ? "bg-primary justify-end" : "bg-border justify-start"
              }`}
            >
              <div className="w-4 h-4 bg-white rounded-full shadow mx-0.5" />
            </div>
          </button>

          {/* Preset rapidi */}
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground font-medium">Formati comuni</p>
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setTempSize({ width: p.w, height: p.h })}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    Number(tempSize.width) === p.w && Number(tempSize.height) === p.h
                      ? "bg-primary text-white border-primary"
                      : "bg-muted text-foreground/70 border-border hover:bg-muted/80"
                  }`}
                  data-testid={`preset-${p.label}`}
                >
                  {p.label}mm
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button type="button" size="sm" onClick={onSave} data-testid="save-label-size-btn">
            Salva formato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LabelSettingsDialog;
